import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Copy, Download, Trash2 } from 'lucide-react';
import { authService } from '../services/authService';
import { buildDownloadName } from '../utils/downloadName';
import { createStreamingSession } from '../services/streamingClient';
import { logUiEvent } from '../services/uiLogger';
import { createResilientSession, PACE_FACTOR } from './streaming/resilientSession';
import useStreamingCapture from './streaming/useStreamingCapture';
import LiveTranscript, { formatMMSS } from './streaming/LiveTranscript';
import { reflowFinals, paragraphsToText } from './streaming/reflow';
import { countWords, paceLabel } from './streaming/sessionStats';
import MicPanel, { MicBar, RecordingBadge } from './streaming/MicPanel';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  describeClose, formatWait,
  RECONNECT_GAVE_UP_MESSAGE, CONNECT_FAILED_MESSAGE, BACKLOG_OVERFLOW_MESSAGE,
} from './streaming/closeCodes';
import { capWarning } from './streaming/sessionCap';
import { MIC_SOURCE_NAME } from '../lib/streamingHistory';
import { streamingStartedAt } from '../lib/meetingDate';
import { ENDPOINTS } from '../config/endpoints';

const STREAMING_API_URL = ENDPOINTS.streaming;
// Как часто сверяться с пределом длительности. Раз в 15 секунд: окно
// предупреждения — десять минут, точнее не нужно, а лишние тики ни к чему.
const CAP_TICK_MS = 15000;
// Сколько ждать после finalize() последних final-кадров перед close() на стопе.
const FINALIZE_GRACE_MS = 1500;
// Состояние связи, пока всё хорошо: плашки нет.
const LINK_OK = { state: 'live', catchingUp: false, backlogSec: 0 };
// Фазы, в которых запись ещё жива. finishing — «Стоп» нажат, микрофон выключен,
// но звук, накопленный за обрыв, ещё досылается: в Историю запись уходит только
// на stopped (lib/streamingHistory.js), поэтому раньше времени stopped не ставим.
const LIVE_PHASES = ['recording', 'paused', 'finishing'];
// Вторая кнопка «Стопа» во время досылки: как обычный «Стоп» до этой правки.
const STOP_NOW_LABEL = 'Остановить сейчас';

// Ключ идемпотентности записи в истории: у live-режима нет taskId (задача на бэкенде
// не создаётся), поэтому запись сама выдаёт себе id при старте. crypto.randomUUID есть
// не везде (старый Safari, http-контекст) — отсюда фолбэк.
const newSessionId = () => (
  window.crypto?.randomUUID?.()
  || `live-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
);

// Звук встречи — по возможностям браузера, а не пробным вызовом: getDisplayMedia
// без клика браузер отклоняет (нужен жест), и кнопка не появлялась никогда; а где
// бы не отклонил — человек видел бы окно выбора экрана при открытии страницы.
// Звук вкладки отдаёт только Chromium (Chrome, Edge, Яндекс); Firefox и Safari
// демонстрацию дают без звука — признак Chromium ≥109 suppressLocalAudioPlayback.
// На телефонах API нет: мобильный браузер звук других приложений не отдаёт.
const systemAudioAvailable = () => {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
  if (typeof md?.getDisplayMedia !== 'function') return false;
  try {
    return 'suppressLocalAudioPlayback' in (md.getSupportedConstraints?.() || {});
  } catch (e) {
    return false;
  }
};

const StreamingTranscription = ({ state, setState, onOpenProtocol, onLeave }) => {
  const isMobile = useIsMobile();
  // recording/paused нельзя восстановить после ремаунта (аудио-сессия не персистится) —
  // при переключении вкладки и возврате понижаем до 'stopped', текст финалов не теряем.
  const wasLive = LIVE_PHASES.includes(state.phase);
  const initialPhase = wasLive ? 'stopped' : (state.phase || 'idle');

  const [phase, setPhase] = useState(initialPhase);
  const [finals, setFinals] = useState(state.finals || []);
  const [partial, setPartial] = useState(wasLive ? '' : (state.partial || ''));
  const [error, setError] = useState(state.error || null);
  const [connectionStatus, setConnectionStatus] = useState(state.connectionStatus || 'соединение неактивно');
  const [audioSource, setAudioSource] = useState(state.audioSource || 'microphone');
  const systemAudioSupported = !isMobile && systemAudioAvailable();
  // Сохранённый выбор «со звуком встречи» там, где его нет (узкое окно, другой
  // браузер), — это микрофон: иначе ни одна кнопка не выбрана, а старт падает.
  const effectiveSource = audioSource === 'system' && !systemAudioSupported ? 'microphone' : audioSource;
  const [elapsedSec, setElapsedSec] = useState(0);
  // Предупреждение о близком пределе длительности. Предел называет сервер в
  // кадре status; пока он молчит, предупреждать не о чем.
  const [capWarn, setCapWarn] = useState(null);
  // Связь с сервером внутри записи: обрыв не останавливает запись, а показывает
  // плашку, пока идёт переподключение и досылка накопленного звука.
  const [link, setLink] = useState(LINK_OK);
  // Тайм-коды [MM:SS] на абзац в Копировать/Скачать; по умолчанию выключены.
  const [withTimecodes, setWithTimecodes] = useState(state.withTimecodes || false);
  // id текущей записи: по нему App сохраняет её в историю и к нему же привязывает
  // протокол (иначе саммари создало бы вторую запись вместо PATCH к этой).
  const [sessionId, setSessionId] = useState(state.sessionId || null);
  // Начало записи — дата встречи для протокола («до пятницы» → число).
  const [startedAt, setStartedAt] = useState(state.startedAt || null);

  // Живая запись (resilientSession) — переживает обрывы: под ней сколько угодно
  // серверных сеансов, для компонента это одна запись.
  const sessionRef = useRef(null);
  const timerRef = useRef(null);
  const scrollRef = useRef(null);      // контейнер живой ленты (автоскролл)
  const stickBottomRef = useRef(true); // липнем к низу, пока юзер сам не отмотал вверх
  const startTimeRef = useRef(null);
  // 🔴 Отдельные стенные часы сессии, не startTimeRef. Тот сдвигается вперёд на
  // длительность паузы, а серверный счётчик паузы не знает вовсе
  // (bit-streaming/app/session.py:63 — от начала сессии по часам). Считая остаток
  // по startTimeRef, мы предупреждали бы позже, чем сервер закрывает — то есть
  // ровно в тех сессиях с паузами, где предупреждение и нужно.
  // Переподключение открывает новый серверный сеанс со своим счётчиком — эти часы
  // не сбрасываются: предел считается на всю запись.
  const wallStartRef = useRef(null);
  const maxSessionSecRef = useRef(null);
  const pauseStartRef = useRef(null);   // Date.now() входа в паузу — для вычета из elapsed
  const closedRef = useRef(false);       // сервер закрыл WS (в т.ч. во время getUserMedia-промпта)
  const phaseRef = useRef(phase);
  const stopAndSaveRef = useRef(() => {});
  const stopNowRef = useRef(() => {});
  // 🔴 Взводится стартом записи, а не фазой. При перемонтировании (уход на
  // другую вкладку и обратно) recording понижается до stopped, и переход по
  // одной лишь фазе выкидывал бы человека в «Протокол» на каждом возврате.
  const autoProtocolRef = useRef(false);

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
  }, []);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Автоскролл живой ленты: новые финалы/партиалы прокручивают вниз, но только
  // пока пользователь у нижнего края — отмотал вверх перечитать → не дёргаем.
  const handleTranscriptScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    stickBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [finals, partial]);

  // Последнее отданное наверх состояние — для ухода с экрана посреди записи (ниже).
  const liftedRef = useRef(null);
  useEffect(() => {
    const next = { phase, finals, partial, error, connectionStatus, audioSource, withTimecodes, sessionId,
      startedAt };
    liftedRef.current = { next, setState };
    setState(next);
  }, [phase, finals, partial, error, connectionStatus, audioSource, withTimecodes, sessionId, startedAt,
    setState]);

  const handlePcm = useCallback((int16) => {
    sessionRef.current?.sendPcm(int16);
  }, []);

  const handleSourceEnded = useCallback(() => {
    // Пользователь отменил шаринг экрана/вкладки прямо из браузерного UI — завершаем как по кнопке.
    stopAndSaveRef.current();
  }, []);

  const capture = useStreamingCapture({
    withSystemAudio: effectiveSource === 'system',
    onPcm: handlePcm,
    onSourceEnded: handleSourceEnded,
  });

  const startRecording = useCallback(async () => {
    setError(null);
    setFinals([]);
    setPartial('');
    // Своя запись — свой ключ истории: иначе новая перезаписала бы предыдущую
    // (autoSaveItem идемпотентен по этому id).
    setSessionId(newSessionId());
    setStartedAt(new Date().toISOString());
    closedRef.current = false;
    setLink(LINK_OK);
    stickBottomRef.current = true; // новая запись — лента снова липнет к низу
    setConnectionStatus('соединение устанавливается…');
    try {
      // sessionRef/capture — общие между записями. Колбэки этой сессии срабатывают
      // только пока она активна (sessionRef.current === session): иначе отложенный
      // колбэк ПРЕДЫДУЩЕЙ сессии (onClose/onError после быстрого рестарта) задел бы
      // уже новую (гонка «вторая запись падает»).
      let session;
      const isCurrent = () => sessionRef.current === session;
      session = createResilientSession({
        // Каждый сеанс (первый и после обрыва) — обычный /v1/ws. Заголовки — функцией:
        // берутся в момент auth-кадра, токен к переподключению может смениться.
        // KC-сессия → Bearer, легаси → token (единый источник, как у всех сервисов).
        connect: (callbacks) => createStreamingSession({
          wsUrl: STREAMING_API_URL,
          authHeaders: () => authService.getAuthHeaders(),
          ...callbacks,
        }),
        beforeReconnect: () => authService.refreshSession?.(),
        log: (event, meta) => logUiEvent(event, { component: 'streaming', meta }),
        onStatus: (msg) => {
          if (!isCurrent()) return;
          setConnectionStatus('соединение установлено');
          // Предел длительности сессии знает только сервер — берём его оттуда.
          if (msg?.max_session_sec) maxSessionSecRef.current = msg.max_session_sec;
        },
        onPartial: (msg) => { if (isCurrent()) setPartial(msg.text || ''); },
        onFinal: (msg) => {
          if (!isCurrent()) return;
          setFinals((prev) => [...prev, { text: msg.text, start: msg.start, end: msg.end, utt: msg.utt }]);
          setPartial('');
        },
        onError: () => { if (isCurrent()) setError('Ошибка WebSocket-соединения.'); },
        onLink: (next) => {
          if (!isCurrent()) return;
          setLink(next);
          if (next.state === 'reconnecting') setConnectionStatus('переподключение…');
        },
        // Конец записи не по «Стопу»: отказ сервера с причиной или связь не
        // вернулась за 5 минут. Обрыв с переподключением сюда не попадает.
        onEnd: ({ code, reason, established, giveUp, cause, connectFailed }) => {
          if (!isCurrent()) return;  // не активная больше запись — не трогаем UI/capture новой
          closedRef.current = true;
          let message;
          if (giveUp) message = cause === 'lagging' ? BACKLOG_OVERFLOW_MESSAGE : RECONNECT_GAVE_UP_MESSAGE;
          else if (connectFailed || (!established && code === 1006)) message = CONNECT_FAILED_MESSAGE;
          else message = describeClose(code, reason, maxSessionSecRef.current);
          if (message) setError(message);
          setLink(LINK_OK);
          setConnectionStatus('соединение неактивно');
          if (LIVE_PHASES.includes(phaseRef.current)) {
            // Сервер закрыл сессию не по нашей команде (idle_timeout/max_session/квота/
            // ошибка) — синхронизируем UI, иначе кнопки продолжают показывать
            // «Запись» при уже мёртвом WS.
            clearInterval(timerRef.current);
            capture.stop();
            phaseRef.current = 'stopped';
            setPhase('stopped');
          }
        },
      });
      sessionRef.current = session;

      await capture.start();

      if (closedRef.current) {
        // Сервер успел закрыть сокет, пока пользователь отвечал на getUserMedia-промпт
        // (auth/quota/busy) — не показываем «Запись» над мёртвым соединением.
        setError((prev) => prev || 'Соединение закрыто до начала записи.');
        sessionRef.current?.close();
        sessionRef.current = null;
        capture.stop();
        setPhase('stopped');
        return;
      }

      startTimeRef.current = Date.now();
      wallStartRef.current = Date.now();
      pauseStartRef.current = null;
      setElapsedSec(0);
      setCapWarn(null);
      startTimer();

      // Запись пошла — значит по её окончании человека ведём в «Протокол».
      autoProtocolRef.current = true;
      setPhase('recording');
    } catch (e) {
      console.error('Ошибка запуска live-записи:', e);
      setError(e?.userMessage || 'Ошибка доступа к источникам звука');
      sessionRef.current?.close();
      sessionRef.current = null;
      capture.stop();
      setPhase('idle');
    }
  }, [capture, startTimer]);

  const pauseRecording = useCallback(() => {
    capture.pause();
    sessionRef.current?.setPaused(true);  // на паузе и ping не шлём — простой считает сервер
    clearInterval(timerRef.current);       // таймер не тикает на паузе
    pauseStartRef.current = Date.now();
    setPhase('paused');
  }, [capture]);

  const resumeRecording = useCallback(() => {
    capture.resume();
    sessionRef.current?.setPaused(false);
    if (pauseStartRef.current) {
      // сдвигаем базу отсчёта вперёд на длительность паузы — elapsed не «прыгает»
      startTimeRef.current += Date.now() - pauseStartRef.current;
      pauseStartRef.current = null;
    }
    startTimer();
    setPhase('recording');
  }, [capture, startTimer]);

  // «Стоп», а звук за обрыв ещё не дослан: микрофон выключаем, запись дописывается
  // (фаза finishing), stopped — когда сервер получил всё и ответил на finalize.
  const finishRecording = useCallback(async (session) => {
    phaseRef.current = 'finishing';
    setPhase('finishing');
    clearInterval(timerRef.current);
    await capture.flush();
    capture.stop();
    session.drain(async () => {
      await new Promise((resolve) => setTimeout(resolve, FINALIZE_GRACE_MS));
      session.close();
      if (sessionRef.current !== session || phaseRef.current !== 'finishing') return;
      phaseRef.current = 'stopped';
      setLink(LINK_OK);
      setPhase('stopped');
      setConnectionStatus('соединение неактивно');
    });
  }, [capture]);

  // «Остановить сейчас» во время досылки (и предел длительности там же): как
  // обычный «Стоп» до этой правки — finalize сразу, недосланный звук выбрасывается.
  const stopNow = useCallback(async () => {
    if (phaseRef.current !== 'finishing') return;
    const session = sessionRef.current;
    phaseRef.current = 'stopped';
    setLink(LINK_OK);
    setPhase('stopped');
    session?.finalize();
    await new Promise((resolve) => setTimeout(resolve, FINALIZE_GRACE_MS));
    session?.close();
    setConnectionStatus('соединение неактивно');
  }, []);

  const stopAndSave = useCallback(async () => {
    if (phaseRef.current !== 'recording' && phaseRef.current !== 'paused') return;
    if (sessionRef.current?.hasBacklog?.()) {
      finishRecording(sessionRef.current);
      return;
    }
    phaseRef.current = 'stopped';
    setPhase('stopped');
    setLink(LINK_OK);
    clearInterval(timerRef.current);
    // Локальная ссылка на ЭТУ сессию — отложенный close() ниже закрывает именно её.
    // sessionRef НЕ зануляем: пока он указывает на эту сессию, isCurrent() пропускает
    // хвостовой PCM из flush и финалы, приходящие в ответ на finalize() в грейс-окне
    // (раннее зануление теряло последнюю фразу). Новая запись, начатая в течение
    // FINALIZE_GRACE_MS, сама перезапишет ref — колбэки этой сессии отсекутся isCurrent().
    const session = sessionRef.current;
    // Дождаться отправки хвоста воркалета (ack 'flushed') ДО finalize() — иначе
    // хвостовой PCM приходит после finalize, стартует новое высказывание и теряется.
    await capture.flush();
    // Микрофон/AudioContext глушим СРАЗУ (не через FINALIZE_GRACE_MS) — иначе отложенный
    // capture.stop() задел бы ресурсы уже новой записи (capture — общий инстанс).
    capture.stop();
    session?.finalize();
    await new Promise((resolve) => setTimeout(resolve, FINALIZE_GRACE_MS));
    session?.close();  // закрываем именно эту сессию, а не текущий (возможно новый) ref
    setConnectionStatus('соединение неактивно');
  }, [capture, finishRecording]);

  useEffect(() => { stopAndSaveRef.current = stopAndSave; }, [stopAndSave]);
  useEffect(() => { stopNowRef.current = stopNow; }, [stopNow]);

  // Предел близко — сказать об этом ЗАРАНЕЕ. Свой тик, а не общий секундомер:
  // тот стоит на паузе, а серверный счётчик сессии паузы не знает.
  useEffect(() => {
    if (!LIVE_PHASES.includes(phase)) { setCapWarn(null); return undefined; }
    const tick = () => {
      const limit = maxSessionSecRef.current;
      if (!limit || !wallStartRef.current) { setCapWarn(null); return; }
      const remaining = limit - (Date.now() - wallStartRef.current) / 1000;
      if (remaining <= 0) {
        // Без обрывов раньше закроет сервер (1000 max_session). После переподключения
        // серверный сеанс моложе записи — предел держим сами, иначе обещание
        // «запись остановится сама» было бы неправдой.
        setError(describeClose(1000, 'max_session', limit));
        // Во время досылки предел — это «Остановить сейчас»: ждать дальше некуда.
        if (phaseRef.current === 'finishing') stopNowRef.current();
        else stopAndSaveRef.current();
        return;
      }
      setCapWarn(capWarning(remaining, limit));
    };
    tick();
    const id = setInterval(tick, CAP_TICK_MS);
    return () => clearInterval(id);
  }, [phase]);

  // Отметка в заголовке вкладки. Плашка на экране до человека не доходит: он в
  // этот момент ведёт встречу, а не смотрит на вкладку — ровно так 14.09 и
  // потерялся хвост записи. Зависимость — признак, а не текст: иначе заголовок
  // переписывался бы каждую минуту вместе с остатком.
  const capWarnActive = !!capWarn;
  useEffect(() => {
    if (!capWarnActive) return undefined;
    const original = document.title;
    document.title = `⚠ ${original}`;
    return () => { document.title = original; };
  }, [capWarnActive]);

  // Закрыть вкладку во время записи — потерять её (случайно закрытая среди
  // других вкладок: обращение f7a35915). Браузер переспросит (текст вопроса он
  // показывает свой) во всех живых фазах, включая досылку звука за обрыв.
  const unsentAudio = phase === 'finishing'
    || ((phase === 'recording' || phase === 'paused') && (link.state === 'reconnecting' || link.catchingUp));
  const guardUnload = LIVE_PHASES.includes(phase);
  useEffect(() => {
    if (!guardUnload) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guardUnload]);

  // Размонтирование (уход со вкладки) — освободить WS/аудио-ресурсы немедленно.
  // 🔴 И отдать наверх запись остановленной: История сохраняет только 'stopped',
  // а без этого наверху навсегда оставалась 'recording' — 19.10.2026 у Владимира
  // так пропали 6,5 минуты встречи, без следа и без вопроса.
  useEffect(() => () => {
    clearInterval(timerRef.current);
    sessionRef.current?.close();
    capture.stop();
    // Вышел из учётки — запись не отдаём: сохранить её без токена некому, а
    // следующий вошедший в этой вкладке получил бы чужой текст.
    const lifted = liftedRef.current;
    if (lifted && LIVE_PHASES.includes(lifted.next.phase) && authService.getTokenData()) {
      lifted.setState({ ...lifted.next, phase: 'stopped', partial: '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Абзацы (пунктуация + паузы) — общая пересборка с live-вью (reflow.js).
  const paragraphs = useMemo(() => reflowFinals(finals), [finals]);
  // Чистый текст абзацами: для «Протокола» (LLM) и счётчика слов — всегда без меток.
  const finalText = useMemo(() => paragraphsToText(paragraphs), [paragraphs]);
  // Копировать/Скачать: те же абзацы, метки по переключателю.
  const exportText = useMemo(
    () => paragraphsToText(paragraphs, { withTimecodes }),
    [paragraphs, withTimecodes]
  );
  // Вместе с живой гипотезой: на монологе без пауз финал приходит только на
  // «Остановить», и до тех пор счётчик показывал бы ноль при полном экране текста.
  const wordsCount = useMemo(() => countWords(finalText, partial), [finalText, partial]);
  const durationLabel = formatMMSS(elapsedSec);
  const finishing = phase === 'finishing';
  const paceText = paceLabel(wordsCount, elapsedSec);
  // «Спишется» из артборда: минуты записи, округлённые ВВЕРХ — так же считает
  // квота, и показывать меньше, чем спишется, нельзя.
  const spendLabel = `${Math.max(1, Math.ceil(elapsedSec / 60))} мин`;

  const handleCopy = () => {
    if (exportText) navigator.clipboard.writeText(exportText);
  };

  const handleSave = () => {
    if (!exportText) return;
    const element = document.createElement('a');
    const file = new Blob([exportText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = buildDownloadName(MIC_SOURCE_NAME, 'trnscr', 'txt');
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleOpenProtocol = () => {
    // sessionId как sourceTaskId: саммари уйдёт PATCH-ом в уже сохранённую запись,
    // а не заведёт вторую (см. эффект линковки протокола в App.js).
    if (finalText) {
      onOpenProtocol(finalText, undefined, MIC_SOURCE_NAME, sessionId, undefined, undefined,
        startedAt || streamingStartedAt(finals));
    }
  };

  // Остановилась свежая запись — открываем «Протокол» сами, без отдельного
  // нажатия: так работает весь остальной поток распознавания. Ждём именно
  // текста: финалы последней фразы приходят уже после «Остановить», в
  // грейс-окне finalize, и переход по одной лишь фазе унёс бы человека
  // с недописанной расшифровкой.
  useEffect(() => {
    if (!autoProtocolRef.current || phase !== 'stopped' || !finalText) return;
    autoProtocolRef.current = false;
    handleOpenProtocol();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, finalText]);

  const handleDelete = () => {
    setFinals([]);
    setPartial('');
    setError(null);
    setPhase('idle');
    setSessionId(null);
    setStartedAt(null);
  };

  // Верстка по «Бумаге»: карточки без рамок, кнопки заливкой, скругления
  // card/panel/control. Своего артборда у живой записи в пакете нет — экран
  // собран на языке остальных (замечание владельца 07.09.2026), расхождение
  // записано в docs/backlog/redesign-shell-gaps.md.
  const ICON_BTN = 'w-8 h-8 rounded-chip bg-paper-bg text-paper-ink hover:bg-paper-panel '
    + 'flex items-center justify-center transition-colors';
  const TEXT_BTN = 'h-8 rounded-chip bg-paper-bg px-3 text-[13px] font-medium text-paper-ink '
    + 'hover:bg-paper-panel transition-colors';

  return (
    <div id="streaming-page" className="flex flex-col h-full gap-3 font-ui">
      {phase === 'stopped' && (
        <div className="flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            id="checkbox-timecodes"
            aria-label="С тайм-кодами"
            aria-pressed={withTimecodes}
            onClick={() => setWithTimecodes(!withTimecodes)}
            title="Добавлять [MM:SS] в начало каждого абзаца при копировании и скачивании"
            className={`h-8 rounded-chip px-3 text-[13px] mr-1 transition-colors ${
              withTimecodes ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}
          >
            С тайм-кодами
          </button>
          <button type="button" id="button-copy" onClick={handleCopy} className={ICON_BTN} title="Копировать">
            <Copy className="w-4 h-4" />
          </button>
          <button type="button" id="button-save" onClick={handleSave} className={ICON_BTN} title="Скачать">
            <Download className="w-4 h-4" />
          </button>
          <button type="button" id="button-delete" onClick={handleDelete} className={ICON_BTN}
                  title="Удалить и начать новую запись">
            <Trash2 className="w-4 h-4" />
          </button>
          <button type="button" id="button-protocol" onClick={handleOpenProtocol} className={TEXT_BTN}>
            Протокол
          </button>
          <button type="button" id="button-new-recording" onClick={startRecording}
            className="h-8 rounded-chip bg-paper-accent px-3 text-[13px] font-semibold text-white hover:bg-paper-accent-hover transition-colors">
            + Новая
          </button>
        </div>
      )}

      {capWarn && (
        <div id="cap-warning" role="status"
             className="rounded-panel bg-paper-bg px-4 py-3 text-[13px] font-medium text-paper-ink shrink-0">
          {capWarn}
        </div>
      )}

      {(unsentAudio || phase === 'finishing') && (
        <div id="reconnect-banner" role="status"
             className="rounded-panel bg-paper-bg px-4 py-3 text-[13px] font-medium text-paper-ink shrink-0">
          {phase === 'finishing' ? (
            <>
              {link.state === 'reconnecting' && <div>Связь прервалась — переподключаемся</div>}
              <div>
                Дописываем текст
                {link.state !== 'reconnecting' && link.backlogSec > 0
                  && ` — ещё ${formatWait(link.backlogSec / PACE_FACTOR)}`}
              </div>
            </>
          ) : (
            <>
              {link.state === 'reconnecting'
                ? 'Связь прервалась — переподключаемся. Запись идёт'
                : 'Связь восстановлена. Запись идёт'}
              {link.catchingUp && (
                <div className="mt-1 font-normal text-paper-ink-2">
                  догоняем распознавание
                  {link.backlogSec > 0 && ` — ещё ${formatWait(link.backlogSec / (PACE_FACTOR - 1))}`}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <div id="error-message" role="alert"
             className="rounded-panel bg-paper-accent/[0.06] px-4 py-3 text-[13px] text-paper-accent shrink-0">
          {error}
        </div>
      )}

      {phase === 'idle' && (
        <div className="flex-1 bg-paper-card rounded-card px-5 py-7 md:px-9 md:py-9 flex flex-col items-center justify-center gap-4 text-center">
          <h1 className="m-0 font-display text-[30px] md:text-[40px] font-extrabold leading-none tracking-[-0.035em] text-paper-ink">
            Записать сейчас
          </h1>
          <p className="m-0 max-w-[44ch] text-[15px] text-paper-ink-2">
            Текст появляется по ходу речи. Остановите — получите расшифровку и протокол.
          </p>

          <div className="mt-1 flex gap-2">
            <button type="button" id="button-source-mic" onClick={() => setAudioSource('microphone')}
              className={`rounded-[10px] px-4 py-[10px] text-[14px] font-medium transition-colors ${
                effectiveSource === 'microphone' ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}>
              Микрофон
            </button>
            {systemAudioSupported && (
              <button type="button" id="button-source-system" onClick={() => setAudioSource('system')}
                className={`rounded-[10px] px-4 py-[10px] text-[14px] font-medium transition-colors ${
                  effectiveSource === 'system' ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`}>
                Микрофон + звук встречи
              </button>
            )}
          </div>
          {effectiveSource === 'system' && (
            <p className="text-[13px] text-paper-ink-3 max-w-[460px]">
              Откроется окно выбора: укажите вкладку со встречей и включите «Поделиться звуком
              вкладки». Встреча в приложении — на Windows «Весь экран» и «Звук системы»; на Mac
              браузеру доступен только звук вкладки.
            </p>
          )}

          <button type="button" id="button-record-toggle" onClick={startRecording}
            className="flex items-center gap-2 rounded-control bg-paper-accent hover:bg-paper-accent-hover px-[26px] py-[14px] text-[15px] font-semibold text-white transition-colors">
            <span className="w-2 h-2 rounded-full bg-paper-card" aria-hidden="true" />
            Начать запись
          </button>

          {/* «К выбору записи» стоит здесь, а не отдельной кнопкой над экраном:
              так в артборде — выход последней строкой карточки. */}
          {onLeave && (
            <button type="button" onClick={onLeave}
              className="text-[13px] text-paper-ink-3 underline underline-offset-[3px] hover:text-paper-ink">
              К выбору записи
            </button>
          )}

          <span id="connection-status" className="sr-only">
            Статус соединения: {connectionStatus}
          </span>
        </div>
      )}

      {/* Телефон — экран «Запись» мобильного макета: строка состояния сверху,
          текст во всю ширину, внизу волна и две липкие кнопки. Колонка 292px
          справа оставляла бы тексту меньше сотни пикселей. */}
      {isMobile && LIVE_PHASES.includes(phase) && (
        <div className="flex flex-1 min-h-0 flex-col gap-3">
          <div className="shrink-0 flex items-center justify-between px-1">
            <RecordingBadge paused={phase === 'paused'} duration={durationLabel}
              label={phase === 'finishing' ? 'Дописываем' : undefined} />
            {spendLabel && <span className="font-mono text-[11px] text-paper-ink-3">спишется {spendLabel}</span>}
          </div>
          <div
            id="transcription-container"
            ref={scrollRef}
            onScroll={handleTranscriptScroll}
            className="flex-1 min-h-0 overflow-y-auto bg-paper-card rounded-[20px] px-5 py-5 text-[16px]"
          >
            <LiveTranscript finals={finals} partial={partial} />
          </div>
          <MicBar
            level={finishing ? 0 : capture.level}
            paused={phase === 'paused'}
            onTogglePause={finishing ? undefined : (phase === 'paused' ? resumeRecording : pauseRecording)}
            onStop={finishing ? stopNow : stopAndSave}
            stopLabel={finishing ? STOP_NOW_LABEL : undefined}
          />
        </div>
      )}

      {!isMobile && LIVE_PHASES.includes(phase) && (
        <div className="grid flex-1 min-h-0 gap-3" style={{ gridTemplateColumns: '1fr 292px' }}>
          <div
            id="transcription-container"
            ref={scrollRef}
            onScroll={handleTranscriptScroll}
            className="overflow-y-auto bg-paper-card rounded-card px-[34px] py-[30px]"
          >
            <LiveTranscript finals={finals} partial={partial} />
          </div>
          <MicPanel
            level={finishing ? 0 : capture.level}
            deviceLabel={capture.deviceLabel}
            paused={phase === 'paused'}
            onTogglePause={finishing ? undefined : (phase === 'paused' ? resumeRecording : pauseRecording)}
            stats={{ duration: durationLabel, words: wordsCount, pace: paceText, spend: spendLabel }}
            onStop={finishing ? stopNow : stopAndSave}
            stopLabel={finishing ? STOP_NOW_LABEL : undefined}
            badge={finishing ? 'Дописываем' : undefined}
          />
        </div>
      )}

      {phase === 'stopped' && (
        <div
          id="transcription-container"
          className="flex-1 min-h-[200px] overflow-y-auto bg-paper-card rounded-card px-5 py-5 md:px-[34px] md:py-[30px]"
        >
          {/* WYSIWYG: экран следует чекбоксу «С тайм-кодами» — что видно, то и копируется */}
          <LiveTranscript finals={finals} partial="" showTimecodes={withTimecodes} />
        </div>
      )}
    </div>
  );
};

export default StreamingTranscription;
